import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { HtmlEditorComponent } from './html-editor/html-editor/html-editor.component';

import * as _ from 'lodash';
import Quill from 'quill';
import { MessengerService } from '@services/messenger/messenger.service';
import { DeeplTranslationService } from '@services/deepl-translation/deepl-translation.service';

const Break: any = Quill.import('blots/break');
const Embed: any = Quill.import('blots/embed');
const Delta: any = Quill.import('delta');
Break.prototype.insertInto = function(parent, ref) {
  Embed.prototype.insertInto.call(this, parent, ref)
};
Break.prototype.length = function() {
  return 1;
};
Break.prototype.value = function() {
  return '\n';
};
Quill.register(Break);

function lineBreakMatcher() {

  const newDelta = new Delta();
  newDelta.insert({ 'break': '' });
  return newDelta;
}

@Component({
    selector: 'editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.scss'],
    providers: [DeeplTranslationService],
    standalone: false
})

export class EditorComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() content: any;
  @Input() mainLanguageContent: any;
  @Input() language: any;
  @Input() options: any;
  @Input() height = '300px';
  @Input() packageId: string;
  @Output() changeContent = new EventEmitter();

  observer!: MutationObserver;
  newContent = '';
  html;
  superadmin = false;

  initTimeout;
  firstTimeInit = false;

  specialButtonPreview;
  public editor;

  public formats = ['bold', 'header', 'italic', 'strike', 'underline', 'link', 'list', 'blockquote', 'indent', 'align'];
  public embedded = ['image', 'break'];

  public config = {
    toolbar: {
      container: [
        [{ 'header': [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [false, 'right', 'center'] }],
        ['link'],
        ['link-tel', 'link-email'],
        ['clean'], // remove formatting button
      ],
    },
    clipboard: {
      matchVisual: false,
      matchers: [
        ['BR', lineBreakMatcher]
      ],
    },
    keyboard: {
      bindings: {
        'list autofill': null,
        linebreak: {
          key: 13,
          shiftKey: true,
          handler: function(range) {
            const currentLeaf = this.quill.getLeaf(range.index)[0];
            const nextLeaf = this.quill.getLeaf(range.index + 1)[0];
            this.quill.insertEmbed(range.index, 'break', true, 'user');
            if (nextLeaf === null || (currentLeaf.parent !== nextLeaf.parent)) {
              this.quill.insertEmbed(range.index, 'break', true, 'user');
            }
            this.quill.setSelection(range.index + 1, Quill.sources.SILENT);
          }
        }
      }
    }
  };

  constructor(private snackbar: SnackbarService, private dialog: MatDialog, private ms: MessengerService, private translation: DeeplTranslationService, private elRef: ElementRef) {
    this.ms.$superadminPrivileges.subscribe(privileges => {
      this.superadmin = !!(privileges || []).find(el => el.privilegeName === 'superadmin' && el.value === 'packages');
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const contentEl = document.getElementsByClassName('content');
    let elIndex;
    let maxHeight = 0;
    for (let i = 0; i < contentEl.length; i++) {
      if (maxHeight < contentEl[i].scrollHeight) {
        maxHeight = contentEl[i].scrollHeight;
        elIndex = i;
      }
    }
    if (event.key === 'PageUp') {
      event.preventDefault();
      if (elIndex) {
        contentEl[elIndex].scrollTo(0, 0);
      }
    }
    if (event.key === 'PageDown') {
      event.preventDefault();
      if (elIndex) {
        contentEl[elIndex].scrollTo(0, maxHeight);
      }
    }
  }

  ngOnInit() {

    if (this.options) { // overwrites config for quill editor
      this.config.toolbar.container = this.options.toolbar;
      if (this.options.formats) {
        this.formats = this.options.formats;
      }
      if (this.options.embedded) {
        this.embedded = this.options.embedded;
      }
    }
  }

  ngAfterViewInit() {
    this.initTimeout = setTimeout(() => {
      const customButtonTel = document.querySelector('.ql-link-tel');
      const telHtml = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-telephone-fill" viewBox="0 0 16 16">\n<path fill-rule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>\n</svg>';
      this.addLinkButton(customButtonTel, telHtml, 'tel');

      const customButtonEmail = document.querySelector('.ql-link-email');
      const mailHtml = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-envelope" viewBox="0 0 16 16">\n<path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>\n</svg>';
      this.addLinkButton(customButtonEmail, mailHtml, 'email');
      const qlTooltip = document.querySelector('.ql-tooltip');
      const qlAction = document.querySelector('a.ql-action');
      let value;
      if (document.querySelector('.ql-tooltip input')) {
        document.querySelector('.ql-tooltip input').addEventListener('change', (event: any) => {
          value = event.target.value;
        });
      }
      if (qlTooltip && qlAction) {
        qlAction.addEventListener('click', (event) => {
          if (qlTooltip.classList.contains('ql-editing')) {
            if (qlTooltip.getAttribute('data-mode') === 'link-tel') {
              this.content = this.content.replace(this.specialButtonPreview, `<a href="tel:${(value || this.specialButtonPreview)}">${this.specialButtonPreview}</a>`);
            } else if (qlTooltip.getAttribute('data-mode') === 'link-email') {
              this.content = this.content.replace(this.specialButtonPreview, `<a href="mailto:${(value || this.specialButtonPreview)}">${this.specialButtonPreview}</a>`);
            }
            value = null;
          }
          event.preventDefault();
        });
      }
      const hostElement = this.elRef.nativeElement;

      this.observer = new MutationObserver(() => {
        const containerElement = hostElement.querySelector('.quill-container');
        const childElement = hostElement.querySelector('.ql-tooltip:not(.ql-hidden)');
        if (childElement) {
          let left: any = window.getComputedStyle(childElement)?.left;
          left = parseFloat(left);
          if (left < -20) {
            childElement.style.left = '-20px';
          } else if (left > (containerElement.clientWidth - 273)) {
            childElement.style.left = (containerElement.clientWidth - 273) + 'px';
          }
        }
      });

      this.observer.observe(hostElement, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });
    }, 1000);
  }

  ngOnChanges() {
    if (!this.newContent || !this.newContent.length) {
      this.newContent = this.content;
      this.firstTimeInit = true;
    }
  }

  ngOnDestroy() {
    clearTimeout(this.initTimeout);
  }

  onEditorCreated(editor) {
    this.editor = editor;

    // formats pasted input to allowed nodes and removes all styling
    this.editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      const ops = [];
      delta.ops.forEach(op => {
        if (typeof op.insert === 'string' || _.includes(this.embedded, Object.keys(op.insert)[0])) {
          const attributes = {};
          if (Object.keys(op.insert)[0] === 'break' && op.attributes && op.attributes.hasOwnProperty('bold')) {
            console.log('removed break:', op, node);
          } else {
            _.each(op.attributes, (value, key) => {
              // console.log('each value key: ', value, key);
              if (_.includes(this.formats, key)) {
                attributes[key] = value;
              } else {
                console.log('removing attribute: ', key, value);
              }
            });
            op.attributes = attributes;
            ops.push(op);
          }
        } else {
          const message = typeof op.insert + ' : ' + Object.keys(op.insert)[0];
          console.log('removing invalid object: ', message);
          this.snackbar.open('invalid content removed: ' + message, 5000);
        }
      });
      delta.ops = ops;
      return delta;
    });
  }

  onEditorBlurred(event) {
    if (!event.range && event.oldRange) {
      console.log('editor blured out');
    }
  }

  onChange(event: any) {
    if (this.firstTimeInit) {
      if (this.content !== this.newContent) {
        this.changeContent.emit(event.html);
      }
      this.newContent = event.html;
    }
  }

  public showHTMLPreview() {
    let dialogRef: MatDialogRef<HtmlEditorComponent>;

    dialogRef = this.dialog.open(HtmlEditorComponent, {
      width: '80vw',
      maxWidth: '800px',
      data: this.content ? this.content : ''
    });
    dialogRef.afterClosed().toPromise().then((htmlContent) => {
      if (!htmlContent) {
        return;
      }
      this.content = htmlContent;
    });
  }

  async translateContent() {
    this.content = await this.translation.translateHtml(this.mainLanguageContent, this.language, this.packageId);
    this.onChange({ html: this.content });
  }

  private addLinkButton(button, innerHtml, type) {
    if (button) {
      button.innerHTML = innerHtml;
      button.addEventListener('click', () => {
        const selection = this.editor.getSelection();
        if (selection) {
          this.specialButtonPreview = this.editor.getText(selection);
          if (this.specialButtonPreview) {
            if (this.specialButtonPreview.indexOf(type === 'email' ? 'mailto:' : 'tel:') !== 0) {
              const { tooltip } = this.editor.theme;
              tooltip.edit('link-' + type, this.specialButtonPreview);
            }
          } else {
            this.editor.format('link-' + type, false);
          }
        }
      });
    }
  }
}
